import gql from "graphql-tag";

import type {
    Assignment,
    AssignmentInitialSettings,
    NewAssignment,
    State,
    StudentList,
} from "@khan/assignments-types";
import {
    defaultDueDate as getDefaultAssignmentDueDate,
    getDefaultCourseMasteryDueDate,
    getExerciseId,
    getInitialAssignmentDueDate,
    updateLocalStoreSettings,
} from "@khan/assignments-utils";
import type {ApolloGqlClient, ApolloResult} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {LocalStore} from "@khan/util-local-store";

import type {getExerciseProblemTypeKind} from "./__graphql-types__/getExerciseProblemTypeKind.ts";
import type {
    getClassesAndStudentsType,
    getExerciseProblemTypeKindType,
    khanmigoEnabledForUserQueryType,
} from "./__graphql-types__/index.ts";

// Default settings for new assignments, during creation
// sync-start:content-assignment-storage-key 893837465 apps/khanacademy/src/class-constants-package/constants.ts
export const CONTENT_ASSIGNMENT_STORAGE_KEY =
    "content-assignments-settings_v2" as const;
// sync-end:content-assignment-storage-key

// sync-start:course-mastery-assignment-storage-key 990258018 apps/khanacademy/src/class-constants-package/constants.ts
export const COURSE_MASTERY_ASSIGNMENT_STORAGE_KEY =
    "course-mastery-assignments-settings" as const;
// sync-end:course-mastery-assignment-storage-key

type StateGetter = () => State;

type ActionDispatcher = (
    dispatch: Dispatcher,
    getState?: StateGetter,
) => Promise<any>;

export type Dispatcher = (action: Action | ActionDispatcher) => void;

export type Action =
    | {
          type: "START_EDITING_NEW_ASSIGNMENT";
          assignment: any;
          initialSettings: AssignmentInitialSettings;
          preselectStudents?: boolean;
      }
    | {
          type: "EDIT_ASSIGNMENT";
          assignment: any;
          initialSettings?: AssignmentInitialSettings;
      }
    | {
          type: "EDIT_CLASSDESCRIPTORS";
          classDescriptors: Array<string>;
          initialSettings?: AssignmentInitialSettings;
      }
    | {
          type: "START_EDITING_EXISTING_ASSIGNMENT";
          props: any;
      }
    | {
          type: "START_EDITING_DUPLICATE_ASSIGNMENT";
          props: any;
      }
    | {
          type: "STOP_EDITING_ASSIGNMENT";
      }
    | {
          type: "MUTATION_BEGIN";
      }
    | {
          type: "MUTATION_END";
      }
    | {
          type: "SET_CONTENT_DESCRIPTORS";
          qualifiedContentDescriptorsList: Array<string>;
      }
    | {
          type: "FETCHING_STUDENTLISTS";
      }
    | {
          type: "KHANMIGO_ENABLED";
      }
    | {
          type: "FETCHED_STUDENTLISTS";
          studentLists: Array<StudentList>;
      }
    | {
          type: "HAS_GROUPED_EXERCISES";
      }
    | {
          type: "STANDARD_EXERCISES_ONLY";
      };

export function setContentDescriptorsList(
    qualifiedContentDescriptorsList: Array<string>,
): Action {
    return {
        type: "SET_CONTENT_DESCRIPTORS",
        qualifiedContentDescriptorsList,
    };
}

export function mutationBegin(): Action {
    return {type: "MUTATION_BEGIN"};
}

export function mutationEnd(): Action {
    return {type: "MUTATION_END"};
}

export function newAssignmentWithAssignees(
    classroomDescriptor: string,
    studentKaids: string[],
): Action {
    const action: Action = {
        type: "START_EDITING_NEW_ASSIGNMENT",
        assignment: {
            dueDate: getDefaultAssignmentDueDate(),
        },
        initialSettings: {
            classDescriptors: [classroomDescriptor],
            studentList: {descriptor: classroomDescriptor},
            studentIds: studentKaids,
        },
        preselectStudents: undefined,
    };
    return action;
}

function createAssignment(
    {assignment = {}, preselectStudents, persistSettings = true}: NewAssignment,
    defaultDueDate: Date,
    storageKey: string,
    // Allow for passing a custom LocalStore. Useful for testing.
    localStore: any,
) {
    const initialSettings = localStore.get(storageKey) || {};

    // This is true only from the assignment form horizontal.
    // From content page, there won't be initial setting props in assignment.
    // From Content tab, there'll be current student list so we want to
    // update the sticky settings.
    if (persistSettings) {
        updateLocalStoreSettings(assignment, localStore, storageKey);
    }

    const dueDate = initialSettings.dueDate || defaultDueDate;

    return {
        type: "START_EDITING_NEW_ASSIGNMENT",
        assignment: {
            ...assignment,
            dueDate: getInitialAssignmentDueDate(dueDate),
        },
        initialSettings,
        preselectStudents,
    };
}

export function newAssignment(
    newAssignmentOptions: NewAssignment,
    storageKey: string = CONTENT_ASSIGNMENT_STORAGE_KEY,
    localStore: any = LocalStore,
): Action {
    // @ts-expect-error - TS2322 - Type '{ type: string; assignment: any; initialSettings: any; preselectStudents: boolean | undefined; }' is not assignable to type 'Action'.
    return createAssignment(
        newAssignmentOptions,
        getDefaultAssignmentDueDate(),
        storageKey,
        localStore,
    );
}

export function newCourseMasteryAssignment(
    newAssignmentOptions: NewAssignment,
    storageKey: string = COURSE_MASTERY_ASSIGNMENT_STORAGE_KEY,
    localStore: any = LocalStore,
): Action {
    // @ts-expect-error - TS2322 - Type '{ type: string; assignment: any; initialSettings: any; preselectStudents: boolean | undefined; }' is not assignable to type 'Action'.
    return createAssignment(
        newAssignmentOptions,
        getDefaultCourseMasteryDueDate(),
        storageKey,
        localStore,
    );
}

// TODO (josh): We should probably make the action creators match the action
// types. Right now it's super confusing that the 'editAssignment' creator
// doesn't create the "EDIT_ASSIGNMENT" action.
export function editAssignment(assignment: Assignment): Action {
    return {
        type: "START_EDITING_EXISTING_ASSIGNMENT",
        props: assignment,
    };
}

export function duplicateAssignment(assignment: Assignment): Action {
    return {
        type: "START_EDITING_DUPLICATE_ASSIGNMENT",
        props: assignment,
    };
}

export function stopEditingAssignment(): Action {
    return {
        type: "STOP_EDITING_ASSIGNMENT",
    };
}

export function updateEditedAssignment(
    assignment: any = {},
    // `persistSettings` indicates whether we want to store these latest changes
    // in localStorage or not. We do when creating a new assignment, but not
    // when editing/duplicating one in the coach tools.
    persistSettings = true,
    storageKey: string = CONTENT_ASSIGNMENT_STORAGE_KEY,
    // Allow for passing a custom LocalStore. Useful for testing.
    localStore: any = LocalStore,
): Action {
    if (persistSettings) {
        updateLocalStoreSettings(assignment, localStore, storageKey);
    }

    const initialSettings = localStore.get(storageKey) || {};

    return {
        type: "EDIT_ASSIGNMENT",
        assignment,
        initialSettings,
    };
}

export function updateClassDescriptors(
    classDescriptors: Array<string>,
    // `persistSettings` indicates whether we want to store these latest changes
    // in localStorage or not. We do when creating a new assignment, but not
    // when editing/duplicating one in the coach tools.
    persistSettings = true,
    storageKey: string = CONTENT_ASSIGNMENT_STORAGE_KEY,
    // Allow for passing a custom LocalStore. Useful for testing.
    localStore: any = LocalStore,
): Action {
    if (persistSettings) {
        updateLocalStoreSettings({classDescriptors}, localStore, storageKey);
    }

    const initialSettings = localStore.get(storageKey) || {};

    return {
        type: "EDIT_CLASSDESCRIPTORS",
        classDescriptors,
        initialSettings,
    };
}

export const GET_STUDENT_LISTS_QUERY: GqlOperation<
    getClassesAndStudentsType["response"],
    getClassesAndStudentsType["variables"]
> = gqlOp(gql`
    query getClassesAndStudents {
        coach: user {
            id
            studentLists: coachedStudentLists {
                id
                descriptor
                cacheId
                name
                signupCode
                studentKaidsAndNicknames {
                    id
                    kaid
                    coachNickname
                }
                topics {
                    id
                }
                isChildAssignmentsClassroom
            }
        }
    }
`);

export function fetchClassLists(gqlClient: ApolloGqlClient): ActionDispatcher {
    return (dispatch: Dispatcher) => {
        dispatch({
            type: "FETCHING_STUDENTLISTS",
        });

        return gqlClient
            .query(GET_STUDENT_LISTS_QUERY, {
                fetchPolicy: "network-only",
            })
            .then((result) => {
                const {data} = result;
                const studentLists = data?.coach?.studentLists || [];

                dispatch({
                    type: "FETCHED_STUDENTLISTS",
                    // 1. Array items can be null/undefined in GraphQL data
                    //    but not in the target type of the Action.
                    // 2. Generated types are exact but Action types are not.
                    // 3. __typename is in GraphQL data but not in target type.
                    // @ts-expect-error - TS2322 - Type '(getClassesAndStudents_coach_studentLists | null | undefined)[]' is not assignable to type 'StudentList[]'.
                    studentLists,
                });
            })
            .catch(() => {
                dispatch({
                    type: "FETCHED_STUDENTLISTS",
                    studentLists: [],
                });
            });
    };
}

// Query to see if Khanmigo is enabled for the current user.
export const KHANMIGO_ENABLED_QUERY: GqlOperation<
    khanmigoEnabledForUserQueryType["response"],
    khanmigoEnabledForUserQueryType["variables"]
> = gqlOp(gql`
    query khanmigoEnabledForUserQuery {
        coach: user {
            id
            hasAccessToAIGuideTeacher
            isAIGuideEnabled
        }
    }
`);

export function checkKhanmigoEnabled(
    gqlClient: ApolloGqlClient,
): ActionDispatcher {
    return (dispatch: Dispatcher) => {
        return gqlClient
            .query(KHANMIGO_ENABLED_QUERY, {
                fetchPolicy: "network-only",
            })
            .then((result) => {
                const {data} = result;
                const isAIGuideEnabled =
                    !!data?.coach?.isAIGuideEnabled &&
                    !!data.coach.hasAccessToAIGuideTeacher;
                if (isAIGuideEnabled) {
                    dispatch({
                        type: "KHANMIGO_ENABLED",
                    });
                }
            })
            .catch(() => {
                // TODO(rjcorwin): Implement error in state for notifying in the UI.
            });
    };
}

// TODO (CL-1589): Get rid of this query and fetch problemTypeKind with the rest
// of the content data.
export const GET_EXERCISE_QUERY: GqlOperation<
    getExerciseProblemTypeKindType["response"],
    getExerciseProblemTypeKindType["variables"]
> = gqlOp(gql`
    query getExerciseProblemTypeKind($exerciseIds: [String!]!) {
        exercises: exercisesByIds(ids: $exerciseIds) {
            id
            problemTypeKind
        }
    }
`);

// Given an array of content descriptors, extracts the contentId to see if we
// have any grouped-item exercises (exercise.problemTypeKind === GROUPED)
export function checkHasGroupedExercises(
    gqlClient: ApolloGqlClient,
    contentDescriptors: Array<string>,
): ActionDispatcher {
    return (dispatch: Dispatcher) => {
        const exerciseIds = contentDescriptors.map(getExerciseId);

        const queries: Array<
            Promise<ApolloResult<getExerciseProblemTypeKind>>
        > = [];

        // sync-start:max-content-items 1175710045 https://github.com/Khan/webapp/blob/master/services/content/resolvers/resolver.go
        const batchSize = 50;
        // sync-end:max-content-items
        // The content service only supports queries up to 50 items.
        for (let i = 0; i < exerciseIds.length; i++) {
            queries.push(
                gqlClient.query(GET_EXERCISE_QUERY, {
                    fetchPolicy: "network-only",
                    variables: {
                        exerciseIds: exerciseIds.slice(i, i + batchSize),
                    },
                }),
            );
        }

        if (queries.length > 0) {
            return Promise.allSettled(queries)
                .then((results) => {
                    const hasGroupedExercises = results.some(
                        (result) =>
                            result.status === "fulfilled" &&
                            result.value.data?.exercises?.some(
                                (exercise) =>
                                    exercise?.problemTypeKind === "GROUPED",
                            ),
                    );

                    if (hasGroupedExercises) {
                        dispatch({type: "HAS_GROUPED_EXERCISES"});
                    } else {
                        dispatch({type: "STANDARD_EXERCISES_ONLY"});
                    }
                })
                .catch(() => {
                    dispatch({type: "STANDARD_EXERCISES_ONLY"});
                });
        }
        return Promise.resolve(dispatch({type: "STANDARD_EXERCISES_ONLY"}));
    };
}
